import type { ReactNode } from 'react'

/**
 * A VARAZSLO IKONKESZLETE - INLINE SVG.
 *
 * ────────────────────────────────────────────────────────────────────
 * MIERT INLINE SVG, ES MIERT EGY FAJLBAN?
 *
 * Az oldal eddigi ikonjai (`.header__nav-icon`) CSS-bol, `::before` es
 * `::after` alakzatokbol allnak. Az a technika jol birja a nagyon egyszeru
 * jeleket (harom vonal, kor, rombusz), de egy szemtol vagy egy ceruzatol
 * mar tobb tores kell, mint amennyi egy elemre jut - a `--help` ikon
 * peldaul mar egy `content: '?'` karakterrel csal.
 *
 * Az inline SVG viszont:
 *   - `currentColor`-t orokol, tehat a gomb szovegszinevel egyutt valt;
 *   - nem inditlt halozati kerest (nincs ikonfont, nincs sprite);
 *   - a MERETET a hivo mondja meg, es a vonalvastagsag NEM skalazodik
 *     vele (nincs `vector-effect`), tehat 16 es 32 px kozott is ugyanaz
 *     az optikai suly.
 *
 * ────────────────────────────────────────────────────────────────────
 * A KOZOS SZABALYOK - EGY HELYEN, HOGY NE CSUSSZANAK SZET.
 *
 * Minden ikon ugyanabbol az `Icon` vazbol keszul, ezert nem fordulhat elo,
 * hogy az egyik 24-es, a masik 20-as `viewBox`-ban rajzolodik, vagy hogy
 * valamelyik lekerekitett vegu marad:
 *
 *   viewBox            "0 0 24 24"
 *   stroke             currentColor, vastagsag 2
 *   stroke-linecap     butt      <- NINCS lekerekites
 *   stroke-linejoin    miter     <- NINCS lekerekites
 *   fill               none
 *   aria-hidden        true      <- DISZ, nem tartalom
 *   focusable          false     <- a regi Edge kulonben Tab-celpontta tenne
 *
 * A `butt` vegzodesnek van egy csapdaja: a NULLA HOSSZU vonal ilyenkor
 * SEMMIT nem rajzol. Az `InfoIcon` pontja ezert nem egy `x1==x2, y1==y2`
 * szakasz, hanem egy rovid, de valodi hosszusagu darab.
 *
 * ────────────────────────────────────────────────────────────────────
 * A JEL DISZ, A NEV A GOMBE.
 *
 * Az ikonok `aria-hidden`-ok, tehat a kepernyoolvaso ATLEPI oket. Az
 * ertelmes nevet MINDIG az oket tartalmazo gombra kell tenni:
 *
 *   <button type="button" aria-label="Modell megtekintése"><EyeIcon /></button>
 *
 * Ha az ikon a gomb EGYETLEN tartalma es nincs `aria-label`, a gombnak
 * nincs neve - ez akadalymentessegi hiba, nem szepseghiba.
 * ────────────────────────────────────────────────────────────────────
 */

export interface IconProps {
  /**
   * A negyzet alaku ikon oldalhossza pixelben. Alapertelmezes 20 - ez all
   * jol egy 44-48 px magas gombban.
   */
  size?: number
  /** Tovabbi osztaly a `vx-icon vx-icon--<nev>` MELLE, nem helyette. */
  className?: string
}

interface IconBaseProps extends IconProps {
  /** A `vx-icon--` utotag; egyben a CSS-fogodzo az ikonra. */
  name: string
  children: ReactNode
}

/**
 * A kozos vaz. Kizarolag ITT allnak a rajzolasi tulajdonsagok, hogy egyetlen
 * ikon se tudjon eltavolodni a tobbitol.
 */
function Icon({ name, size = 20, className, children }: IconBaseProps): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className ? `vx-icon vx-icon--${name} ${className}` : `vx-icon vx-icon--${name}`}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="butt"
      strokeLinejoin="miter"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  )
}

/**
 * MEGTEKINTES - szem.
 *
 * Lapos hatszog + benne egy negyzet pupilla. A hatszog SZANDEKOSAN szogletes:
 * a szokasos ket iv (`mandorla`) lagy formanyelv lenne, amit ez az arculat
 * nem hasznal. A fekvo, lapos aranyt egyetlen mas ikon sem viszi - a
 * fejlecben levo `--material` rombusz allo es kicsi, a `--price` kor pedig
 * atlos vonallal jon.
 */
export function EyeIcon(props: IconProps): JSX.Element {
  return (
    <Icon name="eye" {...props}>
      <path d="M1.5 12 L6.5 6 H17.5 L22.5 12 L17.5 18 H6.5 Z" />
      <rect x="10" y="10" width="4" height="4" />
    </Icon>
  )
}

/**
 * SUTI - a hozzajarulasi panel nyitogombja.
 *
 * Kor, benne harom kis negyzet (a „csokidarabok"). A kor itt megengedett:
 * a fejlec `--price` ikonja is korbol indul, tehat nem idegen a
 * formanyelvtol - egy szogletes suti viszont nem lenne felismerheto, es
 * ennek az ikonnak EPP az azonnali felismerhetoseg a dolga.
 *
 * A darabok teli negyzetek, nem korok: igy a kerek korvonal es a szogletes
 * belso ugyanazt a kontrasztot adja, mint a szem hatszoge es negyzet
 * pupillaja.
 */
export function CookieIcon(props: IconProps): JSX.Element {
  return (
    <Icon name="cookie" {...props}>
      <circle cx="12" cy="12" r="9.5" />
      <rect x="8" y="7.5" width="2.2" height="2.2" fill="currentColor" stroke="none" />
      <rect x="14" y="10.5" width="2.2" height="2.2" fill="currentColor" stroke="none" />
      <rect x="9.5" y="14.5" width="2.2" height="2.2" fill="currentColor" stroke="none" />
    </Icon>
  )
}

/**
 * FIGYELMEZTETES - haromszog felkialtojellel.
 *
 * A nyomtathatosagi megallapitasokat nyitja meg minden képernyőméreten.
 * A teljes szöveg kizárólag a hozzá tartozó modálban jelenik meg.
 *
 * Egyenes elek, eles csucsok - ugyanaz a szogletes formanyelv, mint a
 * szemnel es a szemetesnel. A pont NEM vonallal keszul: a kozos vaz
 * `strokeLinecap="butt"` beallitasa mellett egy nulla hosszusagu szakasz
 * egyaltalan nem rajzolodna ki. Ezert kis teli negyzet.
 */
export function WarningIcon(props: IconProps): JSX.Element {
  return (
    <Icon name="warning" {...props}>
      <path d="M12 2.5 L22.5 20.5 H1.5 Z" />
      <path d="M12 9 V14.5" />
      <rect x="11" y="16.5" width="2" height="2" fill="currentColor" stroke="none" />
    </Icon>
  )
}

/**
 * TORLES - szemetes.
 *
 * Fedel + fogantyu + lefele keskenyedo test, benne ket fuggoleges vonal.
 * A keskenyedes nem disz: ettol nem nezheto egy sima doboznak vagy
 * feltoltes-ikonnak.
 */
export function TrashIcon(props: IconProps): JSX.Element {
  return (
    <Icon name="trash" {...props}>
      <path d="M3 6 H21" />
      <path d="M9.5 6 V3 H14.5 V6" />
      <path d="M5.5 6 L6.6 21 H17.4 L18.5 6" />
      <path d="M10 10 V17.5" />
      <path d="M14 10 V17.5" />
    </Icon>
  )
}

/**
 * MODOSITAS - ceruza.
 *
 * Atlos test hegyes csuccsal a bal also sarokban, es egy harant vonal a
 * felso vegen (a szoritogyuru). A harant vonal nelkul a forma egy sima
 * paralelogramma lenne - a gyuru mondja meg, hogy melyik veg a hegy.
 */
export function PencilIcon(props: IconProps): JSX.Element {
  return (
    <Icon name="pencil" {...props}>
      <path d="M17 3.5 L20.5 7 L8 19.5 L3.5 20.5 L4.5 16 Z" />
      <path d="M14 6.5 L17.5 10" />
    </Icon>
  )
}

/**
 * INFORMACIO - negyzet, benne "i".
 *
 * A fejlecben mar all egy magyarazo ikon (`--help`): LEKEREKITETT sarku
 * negyzet, benne KERDOJEL. Ez itt szandekosan mas: ELES sarok, es "i"
 * betu. A ketto igy sem alakban, sem jelentesben nem esik egybe - a
 * kerdojel a GYIK-ra visz, ez pedig egy adott mezo magyarazatat nyitja.
 *
 * A pontot NEM nulla hosszu vonallal rajzoljuk: `stroke-linecap: butt`
 * mellett az semmit nem jelenitene meg (lasd a fajl elejen).
 */
export function InfoIcon(props: IconProps): JSX.Element {
  return (
    <Icon name="info" {...props}>
      <path d="M4 4 H20 V20 H4 Z" />
      <path d="M12 7.5 V9.5" />
      <path d="M12 11.5 V16.5" />
    </Icon>
  )
}

/**
 * BEZARAS - X.
 *
 * Ket atlo, sarokig kifuttatva. A meretet a modalis fejlece 18-ra allitja;
 * kisebb meretnel a ket atlo metszese elmosodna.
 */
export function CloseIcon(props: IconProps): JSX.Element {
  return (
    <Icon name="close" {...props}>
      <path d="M5 5 L19 19" />
      <path d="M19 5 L5 19" />
    </Icon>
  )
}
